import {http} from "../../../shared/api/http"

export type CourseDetail = {
  id: string
  title: string
  description: string
  cover_image?: string
  level: "beginner" | "intermediate" | "advanced"
  duration_minutes?: number
  language: string
  requires_experience: boolean
  price: {
    type: "free" | "paid" | "mixed"
    amount: number
  }
  author: {
    full_name: string
    avatar?: string | { original_url?: string }
  },
  categories: {
    id: string
    title: string
  }[]
  mentors: {
    full_name: string
    avatar?: string | { original_url?: string }
  }[]
  lessons_count: number
  sections: {
    title: string
    items: string[]
  }[]
     is_promoted: boolean
     is_active?: boolean
}

export type CreateCoursePayload = {
  title: string
  description: string
  cover_image_id: string | null
  level: "beginner" | "intermediate" | "advanced"
  duration_minutes: number
  language: string
  requires_experience: boolean
  price: {
    type: "free" | "paid" | "mixed"
    amount: number
  }
  author_id: string | null
  mentor_ids: string[]
  category_ids: string[]
  sections: {
    title: string
    items: string[]
  }[]
}

export type CoursePrice = {
    type: "free" | "paid" | "mixed"
    amount: number
}

export type CourseItem = {
    id: string
    title: string
    description?: string
    cover_image?: string
    duration_minutes?: number
    language: string
    price: CoursePrice
    category_ids: string[]
    is_promoted: boolean
    is_active?: boolean
}

export type CourseListResponse = {
    items: CourseItem[]
    total: number
}

export const listCoursesApi = (
    limit = 10,
    offset = 0,
    author_id?: string
) =>
    http.get<CourseListResponse>("/api/courses", {
        params: {limit, offset, author_id},
    })

/** Full admin course shape — same fields on both the list item and the single-course card. `sections` is always `[]` in the list. */
export type AdminCourseItem = {
    id: string
    title: string
    description: string
    level: "beginner" | "intermediate" | "advanced"
    duration_minutes: number
    language: string
    requires_experience: boolean
    price: CoursePrice
    category_ids: string[]
    mentor_ids: string[]
    is_active: boolean
    cover_image?: string
    is_promoted: boolean
    sections: {
        title: string
        items: string[]
    }[]
}

export type AdminCourseListResponse = {
    items: AdminCourseItem[]
    total: number
}

export const listAdminCoursesApi = (
    limit = 20,
    offset = 0,
    mentor_id?: string
) =>
    http.get<AdminCourseListResponse>("/api/admin/courses", {
        params: {limit, offset, mentor_id},
    })

export const getAdminCourseApi = (id: string) =>
    http.get<AdminCourseItem>(`/api/admin/courses/${id}`)

export const deleteCourseApi = (id: string) =>
    http.delete<{ success: boolean }>(
        `/api/admin/courses/${id}/delete`
    )

export type UploadCoverResponse = {
  id: string
  url?: string
}

export const uploadCourseCoverApi = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return http.post<UploadCoverResponse>(
    "/api/admin/courses/upload/cover-image",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  )
}

export const createCourseApi = (payload: CreateCoursePayload) =>
  http.post<{ id: string }>(
    "/api/admin/courses/create",
    payload
  )

export const getCourseApi = (id: string) =>
  http.get<CourseDetail>(
    `/api/courses/${id}`
  )


export type CourseLessonItem = {
    id: string
    title: string
    description?: string
    order?: number
}

export type CourseLessonsResponse = {
    items: CourseLessonItem[]
    total?: number
}

export const listCourseLessonsApi = (courseId: string) =>
    http.get<CourseLessonsResponse>(
        `/api/courses/${courseId}/lessons`
    )


export type AdminCourseUpdatePayload = {
  title?: string
  description?: string
  level?: "beginner" | "intermediate" | "advanced"
  duration_minutes?: number
  language?: string
  requires_experience?: boolean
  price?: CoursePrice
  is_promoted?: boolean
  is_active?: boolean
  category_ids?: string[]
  mentor_ids?: string[]
  sections?: {
    title: string
    items: string[]
  }[]
}

export const updateCourseApi = (
  id: string,
  payload: AdminCourseUpdatePayload
) =>
  http.patch<AdminCourseItem>(
    `/api/admin/courses/${id}/update`,
    payload
  )

/* ── Mentor content editing ── */

export type MentorCourseUpdatePayload = {
  title?: string
  description?: string
  level?: "beginner" | "intermediate" | "advanced"
  duration_minutes?: number
  language?: string
  requires_experience?: boolean
  sections?: {
    title: string
    items: string[]
  }[]
}

export const updateMentorCourseApi = (
  id: string,
  payload: MentorCourseUpdatePayload
) =>
  http.patch<CourseDetail>(
    `/api/mentor/courses/${id}`,
    payload
  )

